/// <reference types="google.maps" />
import * as React from 'react';
import {
  AdvancedMarker,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { ClustererContext } from './KoobMapClusterer';
import { KoobMapPopup } from './KoobMapPopup';
import { FitMarkersContext, OpenPopupContext } from './KoobMap';

type Props = {
  lat: number | string;
  lng: number | string;
  /** Pin image, usually from @koob/map-marker. */
  icon: string;
  /** Pin image while the popup is open. */
  activeIcon?: string;
  /** Shown as a badge on the pin: a step number, or small icons. */
  label?: React.ReactNode;
  title?: string;
  zIndex?: number;
  /** Content of the popup opened by a click on the pin. */
  popup?: React.ReactNode;
  popupMaxWidth?: number;
  /**
   * The agent cannot pick this pin. The caller greys its icons; a group made
   * only of muted pins turns grey too.
   */
  muted?: boolean;
  onClick?: () => void;
};

// Open pins sit above their neighbours (map-internal stacking).
const OPEN_Z_INDEX = 1000;

let nextKey = 0;

/**
 * A KOOB pin. Inside a KoobMapClusterer it joins the clusters on its own.
 */
export const KoobMapMarker = React.forwardRef<
  google.maps.marker.AdvancedMarkerElement,
  Props
>(function KoobMapMarker(
  {
    lat,
    lng,
    icon,
    activeIcon,
    label,
    title,
    zIndex,
    popup,
    popupMaxWidth = 280,
    muted = false,
    onClick,
  },
  ref,
) {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const clusterer = React.useContext(ClustererContext);
  const register = clusterer?.register;
  const key = React.useRef(`koob-marker-${nextKey++}`);
  const [open, setOpen] = React.useContext(OpenPopupContext);
  const isShown = open?.keys[open.index] === key.current;
  // Opened from a group: the pin itself is hidden in the group bubble.
  const group = isShown && open && open.keys.length > 1 ? open : null;
  const isOpen = isShown && !group;
  const close = React.useCallback(() => setOpen(null), [setOpen]);
  const hasPopup = Boolean(popup);

  React.useEffect(() => {
    const popupKeys = clusterer?.popupKeys;
    if (!popupKeys || !hasPopup) {
      return undefined;
    }
    popupKeys.add(key.current);

    return () => {
      popupKeys.delete(key.current);
    };
  }, [clusterer, hasPopup]);

  const refreshGroups = clusterer?.refresh;

  React.useEffect(() => {
    if (!marker) {
      return;
    }
    marker.dataset.koobMuted = String(muted);
    refreshGroups?.();
  }, [marker, muted, refreshGroups]);

  const setRefs = React.useCallback(
    (element: google.maps.marker.AdvancedMarkerElement | null) => {
      markerRef(element);
      register?.(key.current, element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    },
    [markerRef, register, ref],
  );

  const position = { lat: parseFloat(`${lat}`), lng: parseFloat(`${lng}`) };
  const isPlaced =
    Number.isFinite(position.lat) && Number.isFinite(position.lng);
  const registerPosition = React.useContext(FitMarkersContext);

  React.useEffect(() => {
    if (!registerPosition || !isPlaced) {
      return undefined;
    }
    registerPosition(key.current, { lat: position.lat, lng: position.lng });

    return () => registerPosition(key.current, null);
  }, [registerPosition, isPlaced, position.lat, position.lng]);

  if (!isPlaced) {
    return null;
  }

  return (
    <>
      <AdvancedMarker
        ref={setRefs}
        position={position}
        title={title}
        zIndex={isOpen ? OPEN_Z_INDEX : zIndex}
        onClick={() => {
          onClick?.();
          if (popup) {
            setOpen(isOpen ? null : { keys: [key.current], index: 0 });
          }
        }}
      >
        <div className="relative origin-bottom transition-transform duration-150 hover:scale-110">
          <img
            src={isOpen && activeIcon ? activeIcon : icon}
            width={36}
            height={48}
            alt=""
            draggable={false}
            className="block"
          />
          {label !== undefined && label !== null && label !== '' && (
            <span className="absolute -right-1.5 -top-1 flex h-[18px] min-w-[18px] items-center justify-center gap-0.5 rounded-full border border-solid border-white bg-[#1B2C49] px-1 text-[10px] font-bold leading-none text-white shadow-sm">
              {label}
            </span>
          )}
        </div>
      </AdvancedMarker>

      {isOpen && popup && marker && (
        <KoobMapPopup anchor={marker} maxWidth={popupMaxWidth} onClose={close}>
          {popup}
        </KoobMapPopup>
      )}

      {group && popup && (
        <KoobMapPopup
          lat={group.position?.lat ?? position.lat}
          lng={group.position?.lng ?? position.lng}
          maxWidth={popupMaxWidth}
          pager={{
            index: group.index,
            count: group.keys.length,
            onChange: (index) => setOpen({ ...group, index }),
          }}
          onClose={close}
        >
          {popup}
        </KoobMapPopup>
      )}
    </>
  );
});
